import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  X, Upload, Link2, FileText, Globe, Loader2, CheckCircle, AlertTriangle,
  Download, PlayCircle, ShieldCheck, RefreshCw, Database, ArrowRight, ArrowLeft, Zap, Sparkles,
} from "lucide-react";

const PROVIDERS = [
  { id: "m3u_url", label: "M3U URL", icon: Link2 },
  { id: "m3u_file", label: "Upload .m3u", icon: Upload },
  { id: "m3u_text", label: "Raw Text", icon: FileText },
  { id: "iptv_org", label: "IPTV-ORG", icon: Globe },
];

const ACTION_BADGE = {
  create: "bg-green-500/15 text-green-400 border-green-500/30",
  update: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  backup: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

function Stat({ label, value, tone = "text-white" }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3" data-testid={`import-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={cn("text-2xl font-bold", tone)}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function ImportChannels({ onClose, onDone }) {
  const [step, setStep] = useState(1); // 1 source, 2 preview, 3 result
  const [provider, setProvider] = useState("iptv_org");
  const [metadataMode, setMetadataMode] = useState("country");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [preview, setPreview] = useState(null);
  const [filters, setFilters] = useState({ search: "", country: "", category: "", language: "" });
  const [dupAction, setDupAction] = useState("backup");

  const [report, setReport] = useState(null);
  const [createdIds, setCreatedIds] = useState([]);
  const [scan, setScan] = useState(null);
  const [publishOnlyHealthy, setPublishOnlyHealthy] = useState(false);
  const [publishedCount, setPublishedCount] = useState(null);
  const [masterMsg, setMasterMsg] = useState("");
  const [pipeline, setPipeline] = useState(null); // job doc when running/done
  const [pipelineKind, setPipelineKind] = useState("pipeline");

  const pollPipeline = async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/channels/import/pipeline/status/${jobId}`, { withCredentials: true });
      setPipeline(res.data);
      if (res.data.status === "running") {
        setTimeout(() => pollPipeline(jobId), 2000);
      } else {
        onDone?.();
      }
    } catch { setTimeout(() => pollPipeline(jobId), 3000); }
  };

  useEffect(() => {
    axios.get(`${API}/admin/channels/import/pipeline/latest`, { withCredentials: true })
      .then(r => {
        const j = r.data;
        if (j && j.status === "running") {
          // Only resume a genuinely-active (recent) job so a stuck one never hides step 1.
          const started = j.started_at ? new Date(j.started_at).getTime() : 0;
          if (Date.now() - started < 15 * 60 * 1000) {
            setPipelineKind(j.type === "enrich" ? "enrich" : "pipeline");
            setStep("pipeline");
            pollPipeline(j.id);
          }
        }
      }).catch(() => {});
  }, []);

  const startPipeline = async (kind) => {    setBusy(true); setErr(""); setPipelineKind(kind);
    try {
      const path = kind === "enrich" ? "auto-enrich" : "import/pipeline";
      const res = await axios.post(`${API}/admin/channels/${path}`, {}, { withCredentials: true });
      setStep("pipeline");
      pollPipeline(res.data.job_id);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to start"); }
    setBusy(false);
  };

  const onFile = (e) => {    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(reader.result || "");
    reader.readAsText(f);
  };

  const runPreview = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        provider, url, text,
        metadata_mode: provider === "iptv_org" ? metadataMode : "custom",
        filters: {
          search: filters.search,
          countries: filters.country ? [filters.country] : [],
          categories: filters.category ? [filters.category] : [],
          languages: filters.language ? [filters.language] : [],
        },
      };
      const res = await axios.post(`${API}/admin/channels/import/preview`, body, { withCredentials: true });
      setPreview(res.data);
      setStep(2);
    } catch (e) {
      setErr(e.response?.data?.detail || "Preview failed");
    }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      const res = await axios.post(`${API}/admin/channels/import/commit`,
        { session_id: preview.session_id, dup_action: dupAction }, { withCredentials: true });
      setReport(res.data.report);
      setCreatedIds(res.data.created_channel_ids || []);
      setStep(3);
    } catch (e) {
      setErr(e.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const runScan = async () => {
    if (!createdIds.length) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/channels/import/health-scan`,
        { channel_ids: createdIds }, { withCredentials: true });
      setScan(res.data);
    } catch (e) { setErr(e.response?.data?.detail || "Scan failed"); }
    setBusy(false);
  };

  const publish = async () => {
    if (!createdIds.length) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/channels/import/publish`,
        { channel_ids: createdIds, only_healthy: publishOnlyHealthy }, { withCredentials: true });
      setPublishedCount(res.data.published);
      onDone?.();
    } catch (e) { setErr(e.response?.data?.detail || "Publish failed"); }
    setBusy(false);
  };

  const masterImport = async (kind) => {
    setBusy(true); setMasterMsg("");
    try {
      const res = await axios.post(`${API}/admin/import/${kind}-master`, {}, { withCredentials: true });
      setMasterMsg(kind === "countries"
        ? `Countries: ${res.data.created} created, ${res.data.updated} updated`
        : `${kind[0].toUpperCase() + kind.slice(1)}: ${res.data.created} created`);
    } catch (e) { setMasterMsg(e.response?.data?.detail || "Master import failed"); }
    setBusy(false);
  };

  const exportReport = (fmt) => {
    window.open(`${API}/admin/import-reports/${report.id}/export?format=${fmt}`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="import-wizard">
      <div className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-4xl my-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-blue-400" />
            <h3 className="text-lg font-bold text-white">Import Channels</h3>
            {step !== "pipeline" && <span className="text-xs text-slate-500 ml-2">Step {step} of 3</span>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400" data-testid="import-close"><X size={18} /></button>
        </div>

        <div className="p-6">
          {err && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="import-error">
              <AlertTriangle size={15} /> {err}
            </div>
          )}

          {/* PIPELINE / AUTO-ENRICH PROGRESS */}
          {step === "pipeline" && pipeline && (
            <div className="space-y-4" data-testid="pipeline-view">
              <div className="flex items-center gap-2">
                {pipeline.status === "running" ? <Loader2 size={18} className="animate-spin text-blue-400" />
                  : pipeline.status === "completed" ? <CheckCircle size={18} className="text-green-400" />
                  : <AlertTriangle size={18} className="text-red-400" />}
                <span className="font-semibold text-white">
                  {pipelineKind === "enrich" ? "Auto-Enriching Existing Channels" : "Full IPTV-ORG Import"}
                </span>
                <span className="text-xs text-slate-500 ml-1 capitalize">· {pipeline.phase}</span>
              </div>
              {pipeline.status === "running" && (
                <div>
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all" style={{ width: `${pipeline.total ? Math.min(100, Math.round(pipeline.processed / pipeline.total * 100)) : 5}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1" data-testid="pipeline-progress">{pipeline.processed} / {pipeline.total || "…"} processed</p>
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                <Stat label="New Drafts" value={pipeline.report?.imported || 0} tone="text-green-400" />
                <Stat label="Enriched" value={pipeline.report?.updated || 0} tone="text-blue-400" />
                <Stat label="Backups" value={pipeline.report?.backups_added || 0} tone="text-amber-400" />
                <Stat label="Logos" value={pipeline.report?.logos_added || 0} />
                <Stat label="Countries" value={pipeline.report?.countries_assigned || 0} />
                <Stat label="Categories" value={pipeline.report?.categories_assigned || 0} />
                <Stat label="Languages" value={pipeline.report?.languages_assigned || 0} tone="text-violet-400" />
                {pipelineKind === "enrich" && <Stat label="Skipped" value={pipeline.report?.skipped || 0} />}
              </div>
              {pipeline.status === "completed" && pipeline.report_id && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Report:</span>
                  {["csv", "xlsx", "pdf"].map(f => (
                    <button key={f} onClick={() => window.open(`${API}/admin/import-reports/${pipeline.report_id}/export?format=${f}`, "_blank")}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5" data-testid={`pipeline-export-${f}`}>
                      <Download size={12} /> {f.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => { setPipeline(null); setStep(1); }} data-testid="pipeline-new-import"
                  className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm flex items-center gap-1.5 hover:text-white">
                  <ArrowLeft size={14} /> Import a different source
                </button>
                <button onClick={onClose} disabled={pipeline.status === "running"} data-testid="pipeline-done"
                  className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-40">
                  {pipeline.status === "running" ? "Running…" : "Done"}
                </button>
              </div>
            </div>
          )}

          {/* STEP 1 — SOURCE */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-600/15 to-violet-600/10 p-4" data-testid="pipeline-hero">
                <div className="flex items-start gap-3">
                  <Zap className="text-blue-400 mt-0.5 flex-shrink-0" size={20} />
                  <div className="flex-grow">
                    <h4 className="text-white font-semibold text-sm">One-Click Full Import</h4>
                    <p className="text-xs text-slate-400 mt-0.5">Runs Country → Category → Language from IPTV-ORG and enriches every channel (country, categories, languages, backups) as drafts — no duplicates, no manual work.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button onClick={() => startPipeline("pipeline")} disabled={busy} data-testid="pipeline-start-btn"
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500 disabled:opacity-40 flex items-center gap-1.5">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} Run Full Import
                      </button>
                      <button onClick={() => startPipeline("enrich")} disabled={busy} data-testid="auto-enrich-btn"
                        className="px-4 py-2 rounded-lg bg-violet-600/80 text-white text-xs font-semibold hover:bg-violet-600 disabled:opacity-40 flex items-center gap-1.5">
                        <Sparkles size={13} /> Auto-Enrich Existing
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Or import a single source</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="provider-tabs">
                {PROVIDERS.map(p => (
                  <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`provider-${p.id}`}
                    className={cn("flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-xs font-medium transition-colors",
                      provider === p.id ? "bg-blue-600/20 text-blue-400 border-blue-500/40" : "text-slate-400 border-slate-800 hover:text-white")}>
                    <p.icon size={18} /> {p.label}
                  </button>
                ))}
              </div>

              {provider === "iptv_org" && (
                <div data-testid="metadata-mode">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Import Mode (what group-title means)</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[["country", "By Country"], ["category", "By Category"], ["language", "By Language"]].map(([v, l]) => (
                      <button key={v} onClick={() => setMetadataMode(v)} data-testid={`mode-${v}`}
                        className={cn("px-3 py-2 rounded-lg border text-xs font-medium transition-colors",
                          metadataMode === v ? "bg-blue-600/20 text-blue-400 border-blue-500/40" : "text-slate-400 border-slate-800 hover:text-white")}>{l}</button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-2 mt-2">
                    Reads <b>{metadataMode}</b> from IPTV-ORG's <code className="text-blue-400">index.{metadataMode}.m3u</code>. Everything comes in as <b>Draft</b> — nothing is published or deleted automatically. Re-import in another mode to enrich the same channels (no duplicates).
                  </p>
                </div>
              )}
              {provider === "m3u_url" && (
                <input value={url} onChange={e => setUrl(e.target.value)} data-testid="import-url"
                  placeholder="https://example.com/playlist.m3u"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              )}
              {provider === "m3u_file" && (
                <div>
                  <input type="file" accept=".m3u,.m3u8,text/plain" onChange={onFile} data-testid="import-file"
                    className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-xs file:font-medium" />
                  {text && <p className="text-[11px] text-green-400 mt-2">Loaded {text.length.toLocaleString()} characters</p>}
                </div>
              )}
              {provider === "m3u_text" && (
                <textarea value={text} onChange={e => setText(e.target.value)} data-testid="import-text" rows={8}
                  placeholder="#EXTM3U&#10;#EXTINF:-1 ...,Channel Name&#10;http://stream.url"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              )}

              <div className="border-t border-slate-800 pt-4">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">IPTV-ORG Master Reference</div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => masterImport("countries")} disabled={busy} data-testid="master-countries"
                    className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5">
                    <RefreshCw size={12} /> Sync Countries
                  </button>
                  <button onClick={() => masterImport("categories")} disabled={busy} data-testid="master-categories"
                    className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5">
                    <RefreshCw size={12} /> Sync Categories
                  </button>
                  <button onClick={() => masterImport("languages")} disabled={busy} data-testid="master-languages"
                    className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5">
                    <RefreshCw size={12} /> Sync Languages
                  </button>
                  {masterMsg && <span className="text-[11px] text-slate-400" data-testid="master-msg">{masterMsg}</span>}
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={runPreview} disabled={busy || (provider === "m3u_url" && !url) || ((provider === "m3u_file" || provider === "m3u_text") && !text)}
                  data-testid="import-preview-btn"
                  className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 disabled:opacity-40 flex items-center gap-2">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />} Preview
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — PREVIEW */}
          {step === 2 && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                <Stat label="Parsed" value={preview.summary.total_parsed} />
                <Stat label="New" value={preview.summary.new} tone="text-green-400" />
                <Stat label="Matched" value={preview.summary.matched} tone="text-blue-400" />
                <Stat label="Duplicates" value={preview.summary.duplicates} tone="text-amber-400" />
                <Stat label="Langs" value={preview.summary.languages_assigned || 0} tone="text-violet-400" />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="filter-search"
                  placeholder="Search name..." className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white text-xs flex-grow max-w-[180px]" />
                <select value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value }))} data-testid="filter-country"
                  className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white text-xs">
                  <option value="">All countries</option>
                  {preview.facets.countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} data-testid="filter-category"
                  className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white text-xs">
                  <option value="">All categories</option>
                  {preview.facets.categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filters.language} onChange={e => setFilters(f => ({ ...f, language: e.target.value }))} data-testid="filter-language"
                  className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white text-xs">
                  <option value="">All languages</option>
                  {preview.facets.languages.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button onClick={runPreview} disabled={busy} data-testid="apply-filters"
                  className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Apply
                </button>
              </div>

              {/* Duplicate handling */}
              <div className="flex items-center gap-3 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-400">For same channel with a different stream URL:</span>
                <select value={dupAction} onChange={e => setDupAction(e.target.value)} data-testid="dup-action"
                  className="px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-lg text-white text-xs">
                  <option value="backup">Add as Backup Stream (default)</option>
                  <option value="replace">Replace Primary</option>
                  <option value="separate">Keep Separate</option>
                </select>
              </div>

              {/* Item table */}
              <div className="border border-slate-800 rounded-xl overflow-hidden max-h-[320px] overflow-y-auto" data-testid="preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-900 sticky top-0">
                    <tr className="text-slate-500">
                      <th className="text-left px-3 py-2 font-medium">Channel</th>
                      <th className="text-left px-3 py-2 font-medium">Categories</th>
                      <th className="text-left px-3 py-2 font-medium">Country</th>
                      <th className="text-left px-3 py-2 font-medium">Language</th>
                      <th className="text-left px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-800/50">
                        <td className="px-3 py-2 text-white">{it.name}</td>
                        <td className="px-3 py-2 text-slate-400">{(it.categories || []).join(", ") || "—"}</td>
                        <td className="px-3 py-2 text-slate-400">{it.country || "—"}</td>
                        <td className="px-3 py-2 text-slate-400">{it.language_label || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={cn("px-2 py-0.5 rounded-full border text-[10px] font-bold capitalize", ACTION_BADGE[it.action] || ACTION_BADGE.create)}>
                            {it.action}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.truncated && <p className="text-[11px] text-slate-500">Showing first {preview.items.length} of {preview.total_items}. All matching items will be imported.</p>}

              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm flex items-center gap-1.5" data-testid="import-back">
                  <ArrowLeft size={14} /> Back
                </button>
                <button onClick={commit} disabled={busy} data-testid="import-commit-btn"
                  className="px-5 py-2.5 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-500 disabled:opacity-40 flex items-center gap-2">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />} Import as Drafts
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — RESULT */}
          {step === 3 && report && (
            <div className="space-y-4" data-testid="import-result">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle size={20} /> <span className="font-semibold">Import complete</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                <Stat label="Imported" value={report.imported} tone="text-green-400" />
                <Stat label="Updated" value={report.updated} tone="text-blue-400" />
                <Stat label="Merged" value={report.merged} tone="text-amber-400" />
                <Stat label="Backups Added" value={report.backups_added} />
                <Stat label="Categories Created" value={report.categories_created} />
                <Stat label="Languages Created" value={report.languages_created || 0} tone="text-violet-400" />
                <Stat label="Countries Created" value={report.countries_created} />
                <Stat label="Countries Updated" value={report.countries_updated} />
                <Stat label="Warnings" value={(report.warnings || []).length} tone="text-amber-400" />
              </div>

              {/* Health scan + publish */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={runScan} disabled={busy || !createdIds.length} data-testid="scan-btn"
                    className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-xs hover:text-white flex items-center gap-1.5">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Health Scan ({createdIds.length})
                  </button>
                  {scan && <span className="text-[11px] text-slate-400" data-testid="scan-result">Online: {scan.online} · Failed: {scan.failed}</span>}
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <input type="checkbox" checked={publishOnlyHealthy} onChange={e => setPublishOnlyHealthy(e.target.checked)} data-testid="only-healthy" className="rounded border-slate-700" />
                  Publish only channels that passed the health scan
                </label>
                <div className="flex items-center gap-2">
                  <button onClick={publish} disabled={busy || !createdIds.length} data-testid="publish-btn"
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-500 disabled:opacity-40 flex items-center gap-1.5">
                    <PlayCircle size={13} /> Publish Imported Drafts
                  </button>
                  {publishedCount !== null && <span className="text-[11px] text-green-400" data-testid="publish-result">Published {publishedCount} channels</span>}
                </div>
              </div>

              {/* Export */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Download report:</span>
                {["csv", "xlsx", "pdf"].map(f => (
                  <button key={f} onClick={() => exportReport(f)} data-testid={`export-${f}`}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs hover:text-white flex items-center gap-1.5">
                    <Download size={12} /> {f.toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button onClick={onClose} data-testid="import-done" className="px-5 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-medium hover:bg-slate-700">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
